"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/supabase/client";
import { useAuthModal } from "@/components/auth/auth-modal-context";

/**
 * Formular fuer die Kommentarfunktion unter einer Aktienanalyse (Erik-Wunsch
 * 2026-08-04, Vorbild Aktienfinder - dort anonymes Name/E-Mail-Formular, hier bewusst
 * an die bestehende Anmeldung gekoppelt statt eine zweite Identitaet einzufuehren:
 * die Seite selbst verlangt ohnehin schon Max-Tier-Login. State-Machine/Honeypot-Muster
 * wie components/landing/review-form.tsx.
 */
export function CommentForm({ slug }: { slug: string }) {
  const router = useRouter();
  const { openAuthModal } = useAuthModal();
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  const [displayName, setDisplayName] = useState("");
  const [body, setBody] = useState("");
  const [website, setWebsite] = useState(""); // Honeypot, siehe app/api/stock-analysis-comments/route.ts
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await fetch("/api/stock-analysis-comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analysisSlug: slug, displayName, body, website }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setErrorMessage(data?.error ?? null);
        setStatus("error");
        return;
      }
      setBody("");
      setStatus("idle");
      router.refresh(); // laedt AnalysisCommentsSection (Server-Komponente) neu
    } catch {
      setStatus("error");
    }
  }

  if (userId === undefined) return null;

  if (userId === null) {
    return (
      <p className="text-sm text-muted-foreground">
        Melde dich an, um zu kommentieren.{" "}
        <button
          type="button"
          onClick={() => openAuthModal({ mode: "login" })}
          className="font-medium text-secondary hover:text-ink"
        >
          Jetzt anmelden
        </button>
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <label htmlFor="comment-display-name" className="sr-only">
        Anzeigename
      </label>
      <input
        id="comment-display-name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        required
        maxLength={80}
        placeholder="Dein Anzeigename"
        className="rounded-md border border-hairline bg-card px-3 py-2 text-sm text-ink placeholder:text-muted-foreground"
      />
      <label htmlFor="comment-body" className="sr-only">
        Kommentar
      </label>
      <Textarea
        id="comment-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
        rows={3}
        maxLength={2000}
        placeholder="Deine Meinung zu dieser Analyse..."
        className="bg-card"
      />
      {/* Honeypot - fuer sehende Nutzer unsichtbar und ohne Tab-Stop, gleiches Muster
          wie components/landing/review-form.tsx. */}
      <input
        type="text"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />
      <Button type="submit" disabled={status === "loading"} className="self-start">
        {status === "loading" ? "Wird gesendet…" : "Kommentar absenden"}
      </Button>
      {status === "error" && (
        <p className="text-xs text-negative">{errorMessage ?? "Das hat gerade nicht geklappt."}</p>
      )}
    </form>
  );
}
